import {Head, Link, router, usePage} from '@inertiajs/react'
import Layout from "../../components/layout";
import {ArrowLeftIcon, ArrowUpTrayIcon, LockClosedIcon} from "@heroicons/react/24/outline";
import Album from '../../components/album.jsx';
import React, {useCallback, useEffect, useState} from 'react';
import AuthForm from '../../components/authForm.jsx';
import {Modal} from 'react-responsive-modal';
import {Trans, useTranslation} from 'react-i18next';
import {Blurhash} from 'react-blurhash';

const blurhashes = [
    'LCA,tTRWR8RP0J%MxuogNZR$Riof',
    'LEBypnr@aLV]0K%2xus;JBNGNGbF',
    'L9AAXU%NRh%DDgM_xvbL%hM}t3M|',
    'LcGIcYbDt6IU~qj?WBM{Rjt7WBof',
    'LHFYS:~pNHkBRONH-on%I9R4bEX8',
    'LGAAHwt7IVxuD*j@tPR*0LWBxaNG',
    'LFAmPFxuE1of9GWVxaa|0LNGxuR*',
    'LJAJs8oKE1a{NGofoej@0LWV%Lj[',
    'LWIqMtWBE1%20Moe%2WB57NHRkRk',
    'LzJH28f6Rjj[0Mj[ozayNaaye.fQ',
];

const HiddenAlbum = ({reveal, onViewOwn}) => {
    const {t} = useTranslation();

    return (
        <div className="relative grow mt-2 overflow-hidden">
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-5 gap-1">
                {blurhashes.map((hash, i) => (
                    <div key={i} className="overflow-hidden rounded-lg opacity-40">
                        <Blurhash hash={hash} width="100%" height="100%" />
                    </div>
                ))}
            </div>
            <div className="relative flex flex-col items-center justify-start pt-[25%] px-5 pb-5">
                <LockClosedIcon className="w-10 h-10 text-red-500" />
                <h2 className="text-white-foreground font-bold text-2xl mt-3 mb-2 text-center">
                    {t('Come back later!')}
                </h2>
                <p className="text-white-foreground text-center text-sm">
                    <Trans i18nKey="revealComeBack">Photos will be revealed at <span
                        className="font-semibold">{{reveal}}</span>. Please come back then.</Trans>
                </p>
                <button
                    onClick={onViewOwn}
                    className="mt-4 button-backdrop text-white-foreground px-5 py-2 rounded-[10px] font-medium"
                >
                    {t('View your photos')}
                </button>
            </div>
        </div>
    );
};

export default function PhotosIndex({
                                        event,
                                        photos,
                                        backUrl,
                                        aspectRatio,
                                        effects,
                                        currentEffect,
                                        uploadUrl,
                                        allowBack,
                                        reloadUrl,
                                        has_account
                                    }) {
    const {t} = useTranslation();
    const [effect, setEffect] = useState(currentEffect);
    const {url: currentUrl} = usePage();
    const [open, setOpen] = useState(false);
    const [viewOwn, setViewOwn] = useState(false);

    useEffect(() => {
        if (!has_account) {
            setOpen(true);
        }
    }, []);

    const onEffectChange = useCallback((e) => {
        const value = e.target.value;
        setEffect(value);
        const url = new URL(currentUrl, window.location);
        url.searchParams.set('effect_id', value);
        router.visit(url.toString(), {
            only: ['photos'],
            preserveScroll: true,
            preserveState: true,
        });
    }, []);

    const isViewing = event.can_view_photos || viewOwn;
    const title = (viewOwn && !event.can_view_photos) || (event.reveal_type === 'hidden' && !event.is_organizer)
        ? t('Your photos')
        : t('Gallery');
    const reveal = event.reveal_at_formatted;

    return (
        <Layout>
            <Head title={event.name + ' - Camera'} />
            <div className="flex flex-col px-5 h-svh">
                {(event.reveal_type === 'after' || event.reveal_type === 'during') && !has_account && !event.can_view_photos &&
                    <Modal open={open} onClose={() => setOpen(false)} center>
                        <AuthForm isAfter={event.reveal_type === 'after'} event={event} />
                    </Modal>}

                <div className="sticky top-0 z-10 pt-5 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            {allowBack && <Link href={backUrl}>
                                <div className="mr-3 button-backdrop">
                                    <ArrowLeftIcon className="w-[25px] h-[25px] text-white-foreground" />
                                </div>
                            </Link>}
                            <div>
                                <h1 className="text-white-foreground leading-tight text-xl md:text-2xl font-bold">{title}</h1>
                                <span className="text-white-foreground leading-tight text-sm">{event.name}</span>
                            </div>
                        </div>
                        {uploadUrl && <a
                            href={uploadUrl}
                            className="button-backdrop flex items-center gap-1.5 text-white-foreground px-3 py-2"
                        >
                            <ArrowUpTrayIcon className="w-5 h-5" />
                            <span className="text-sm font-medium">{t('Upload')}</span>
                        </a>}
                    </div>

                    <div className="mt-2">
                        <select
                            name="effect"
                            className="w-full mt-1 button-backdrop text-white-foreground"
                            onChange={onEffectChange}
                            value={effect}
                        >
                            {effects.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                        </select>
                    </div>
                </div>

                {event.reveal_type === 'hidden' && !event.is_organizer && (
                    <p className="text-white-foreground text-xs mt-2">
                        {t("The organizer of this event chose to hide other people's photos. You can only view your own photos.")}
                    </p>
                )}
                {viewOwn && !event.can_view_photos && (
                    <p className="text-white-foreground text-xs mt-1">
                        <Trans i18nKey="revealComeBack">Photos will be revealed at <span
                            className="font-semibold">{{reveal}}</span>. Please come back then.</Trans>
                    </p>
                )}

                {!isViewing && event.reveal_type === 'after' ? (
                    <HiddenAlbum reveal={reveal} onViewOwn={() => setViewOwn(true)} />
                ) : (
                    <Album slideshowAccess={event.live_slideshow_enabled} reloadUrl={reloadUrl} event={event}
                           aspectRatio={aspectRatio} photos={photos} />
                )}
            </div>
        </Layout>
    );
}
